import { useEffect, useMemo, useState } from 'react';
import { useForm, UseFormHandleSubmit } from 'react-hook-form';
import { NavLink } from 'react-router-dom';
import copy from 'copy-to-clipboard';
import useQueryState from '../../table/hooks/use-query-state';
import useGetList from '../../../hooks/use-get-list';
import { PermissionEnum } from '../../../base-types';
import { Filters, SchemaFilterType } from '../../table/types';
import PermissionDenied from '../../icons/permission-denied';
import IconLoader from '../../icons/loader';
import Table from '../../table/table';
import {
  integrationLogsColumns,
  integrationLogsColumnsLabels,
  integrationLogsSchema,
} from './variables/integration-logs';
import { IntegrationLogsTypes } from './types';
import {
  EnumExchangeTabs,
  EnumNostroTabs,
  EnumTabs,
  IntegrationLog,
} from '../types';
import formatIntegrationLogs from './helpers/format-integration-logs';
import IconArrowRight from '../../icons/arrow-right';
import IconRefresh from '../../icons/refresh';

import styles from './styles.module.css';

export default function IntegrationLogs({
  getIntegrationLogs,
  integrationLogsIsLoading,
  integrationLogsIsFetching,
  activePermissions,
  integrationLogs,
  integrationLogsPagination,
  activeTab,
  pspPartnerId,
  transactionId,
}: IntegrationLogsTypes) {
  const { handleSubmit } = useForm();
  const [copyIsSuccess, setCopyIsSuccess] = useState(false);

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(
    integrationLogsSchema,
    true,
  );

  const { getListAction, setPage, onFilter, errorList } =
    useGetList<IntegrationLog>({
      getList: getIntegrationLogs,
      onFilterChange,
      onSortChange,
      schema: integrationLogsSchema,
      withoutChangeSearchParams: true,
      itemId: transactionId,
    });

  const onCopy = (text: string) => {
    setCopyIsSuccess(true);

    copy(text);

    setTimeout(() => {
      setCopyIsSuccess(false);
    }, 2000);
  };

  const onRowClick = (id: string) => {
    window.open(`/integration-logs/list/${id}`, '_blank');
  };

  const formattedData = useMemo(() => {
    const data = formatIntegrationLogs(
      integrationLogs as IntegrationLog[],
      onCopy,
      copyIsSuccess,
    );

    return data;
  }, [integrationLogs, sort, filters, copyIsSuccess, onCopy]);

  useEffect(() => {
    if (
      filters &&
      activePermissions?.includes(PermissionEnum.PSP_INTEGRATION_LOGS_LIST) &&
      ((pspPartnerId &&
        (activeTab === EnumTabs.INTEGRATION_LOGS ||
          activeTab === EnumNostroTabs.INTEGRATION_LOGS)) ||
        activeTab === EnumExchangeTabs.INTEGRATION_LOGS)
    ) {
      const nextFilters: Filters = {};

      if (pspPartnerId) {
        nextFilters.pspPartnerIds = {
          value: [pspPartnerId],
          type: SchemaFilterType.STRING,
        };
      }

      getListAction({
        filters: nextFilters,
      });
    }
  }, [activePermissions, activeTab, pspPartnerId]);

  let content;

  if (!activePermissions?.includes(PermissionEnum.PSP_INTEGRATION_LOGS_LIST)) {
    content = <PermissionDenied />;
  } else if (
    activePermissions?.includes(PermissionEnum.PSP_INTEGRATION_LOGS_LIST)
  ) {
    if (integrationLogsIsLoading) {
      content = (
        <div className={styles.integrationLogsLoader}>
          <IconLoader />
        </div>
      );
    } else {
      content = (
        <div className={styles.integrationLogsTable}>
          <Table
            columns={integrationLogsColumns}
            data={formattedData || []}
            onSort={() => {}}
            filters={filters}
            sort={{}}
            columnsLabels={integrationLogsColumnsLabels}
            onFilterChange={(filterValues) => {
              const nextFilters: Filters = {
                ...filterValues,
                pspPartnerIds: {
                  value: [pspPartnerId as string],
                  type: SchemaFilterType.STRING,
                },
              };

              onFilter({
                ...nextFilters,
              });
            }}
            handleSubmit={handleSubmit as UseFormHandleSubmit<Filters>}
            isLoading={integrationLogsIsFetching}
            take="20"
            setPage={(data) => {
              setPage({
                ...data,
                filters: {
                  pspPartnerIds: {
                    value: [pspPartnerId as string],
                    type: SchemaFilterType.STRING,
                  },
                },
              });
            }}
            pagination={integrationLogsPagination}
            error={errorList}
            onRowClick={onRowClick}
            withVerticalScroll
            withScroll
          />
        </div>
      );
    }
  }

  return (
    <>
      <div className={styles.integrationLogsTitle}>
        <div className={styles.integrationLogsTitleLeft}>
          Integration log
          <button
            type="button"
            className={styles.updateListButton}
            onClick={() => {
              onFilter({
                ...filters,
                pspPartnerIds: {
                  value: [pspPartnerId as string],
                  type: SchemaFilterType.STRING,
                },
              });
            }}
          >
            <IconRefresh
              width={14}
              height={14}
              className={integrationLogsIsFetching ? styles.refreshing : ''}
            />
            Refresh
          </button>
        </div>
        <NavLink
          to={`/integration-logs/list?transactionIds=${transactionId}`}
          target="_blank"
          className={styles.allLogsLink}
        >
          Go to all Integration log <IconArrowRight width={12} height={12} />
        </NavLink>
      </div>
      {content}
    </>
  );
}
